import { useEffect } from 'react'
import { useLocation, useParams } from 'react-router-dom'
import { useShallow } from 'zustand/react/shallow'

import BuildAuto from './BuildAuto'
import BuildManual from './BuildManual'
import useRestoreAnalysedFile from '../../../hooks/useRestoreAnalysedFile'
import useNewUpload from '../../../hooks/useNewUpload'

type TRouteParams = {
  fileId: string
}

type Props = {
  buildAutoFormId: string
  buildManualFormId: string
  onBuildAutoSubmitSuccess: () => void
  onBuildManualSubmitSuccess: () => void
}

const Build = (props: Props) => {
  const {
    buildAutoFormId,
    buildManualFormId,
    onBuildAutoSubmitSuccess,
    onBuildManualSubmitSuccess,
  } = props
  const { fileId } = useParams<TRouteParams>()
  const location = useLocation()
  const { manualRequested, setNewUploadState } = useNewUpload(
    useShallow((state) => ({
      manualRequested: state.manualRequested,
      setNewUploadState: state.setState,
    })),
  )
  const { analysedFile } = useRestoreAnalysedFile()
  const searchParams = new URLSearchParams(location.search)
  const shouldForceManualMode = searchParams.get('mode') === 'manual'

  useEffect(() => {
    if (!analysedFile) {
      return
    }
  }, [analysedFile])

  useEffect(() => {
    if (!analysedFile) {
      return
    }

    setNewUploadState({
      manualRequested: shouldForceManualMode || Boolean(analysedFile.manual),
    })
  }, [analysedFile, fileId, setNewUploadState, shouldForceManualMode])

  const hasAutomaticOptions = Boolean(
    analysedFile &&
    ((analysedFile.auto?.availableDTCs.length || 0) > 0 ||
      (analysedFile.auto?.availableMods.length || 0) > 0),
  )

  if (!analysedFile) {
    return (
      <div className="flex flex-1 items-center justify-center p-12">
        <span className="spinner" />
      </div>
    )
  }

  if (!hasAutomaticOptions || manualRequested) {
    return (
      <BuildManual
        analysedFile={analysedFile}
        formId={buildManualFormId}
        manualRequested={manualRequested}
        onRequestAuto={() => setNewUploadState({ manualRequested: false })}
        onSubmitSuccess={onBuildManualSubmitSuccess}
      />
    )
  }

  return (
    <BuildAuto
      analysedFile={analysedFile}
      formId={buildAutoFormId}
      onRequestManual={() => setNewUploadState({ manualRequested: true })}
      onSubmitSuccess={onBuildAutoSubmitSuccess}
    />
  )
}

export default Build
